/* eslint-disable no-console */
'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { safePostHogCapture, safePostHogGetFeatureFlag } from 'utils/posthogHelpers';

const BUILDER_VARIANT_PARAM = 'builder_variant';
const DEFAULT_VARIANT = 'control';

const safePostHog = {
  capture: (eventName, properties) => {
    try {
      if (typeof window !== 'undefined' && window.posthog) {
        window.posthog.capture(eventName, properties);
        return true;
      }
      return safePostHogCapture(eventName, properties);
    } catch (error) {
      console.warn('[PostHog] Capture error:', error);
      return false;
    }
  },
  getFeatureFlag: (flagName) => {
    try {
      if (typeof window !== 'undefined' && window.posthog) {
        return window.posthog.getFeatureFlag(flagName);
      }
      return safePostHogGetFeatureFlag(flagName);
    } catch (error) {
      console.warn('[PostHog] getFeatureFlag error:', error);
      return null;
    }
  },
};

export const getBuilderVariantFeatureFlagName = (offeringSlug) => {
  return `builder-variant-${offeringSlug}`;
};

export const getBuilderVariantFromPostHog = (offeringSlug) => {
  if (typeof window === 'undefined') return null;

  const flagName = getBuilderVariantFeatureFlagName(offeringSlug);
  const variant = safePostHog.getFeatureFlag(flagName);

  if (variant && typeof variant === 'string') {
    return variant;
  }

  const globalFlag = safePostHog.getFeatureFlag('builder-variant');
  if (globalFlag && typeof globalFlag === 'string') {
    return globalFlag;
  }

  return null;
};

export const trackPostHogBuilderExperiment = (offeringSlug, variantKey, additionalData = {}) => {
  const flagName = getBuilderVariantFeatureFlagName(offeringSlug);

  safePostHog.capture('$experiment_started', {
    $experiment_id: `builder_posthog_${offeringSlug}`,
    $experiment_name: `Builder.io Variant - ${offeringSlug}`,
    $variant_id: variantKey,
    $variant_name: variantKey,
    experiment_source: 'posthog_controlled',
    feature_flag: flagName,
    offering_slug: offeringSlug,
    ...additionalData,
  });

  safePostHog.capture('builder_posthog_variant_assigned', {
    offering_slug: offeringSlug,
    variant_key: variantKey,
    feature_flag: flagName,
    ...additionalData,
  });
};

export const usePostHogBuilderVariant = (offeringSlug, options = {}) => {
  const {
    currentVariant = null,
    variantSource = 'default',
    enableRedirect = true,
    onVariantDetermined = null,
  } = options;

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [determinedVariant, setDeterminedVariant] = useState(currentVariant);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const hasTrackedExperiment = useRef(false);
  const hasAttemptedRedirect = useRef(false);

  const buildUrlWithVariant = useCallback(
    (variant) => {
      const params = new URLSearchParams(searchParams?.toString() || '');
      params.set(BUILDER_VARIANT_PARAM, variant);
      return `${pathname}?${params.toString()}`;
    },
    [pathname, searchParams],
  );

  useEffect(() => {
    if (!offeringSlug || typeof window === 'undefined') {
      setIsLoading(false);
      return;
    }

    const urlVariant = searchParams?.get(BUILDER_VARIANT_PARAM);

    if (urlVariant) {
      setDeterminedVariant(urlVariant);
      setIsLoading(false);

      if (!hasTrackedExperiment.current) {
        trackPostHogBuilderExperiment(offeringSlug, urlVariant, {
          variant_source: 'url_param',
        });
        hasTrackedExperiment.current = true;
      }

      if (onVariantDetermined) {
        onVariantDetermined(urlVariant, 'url_param');
      }
      return;
    }

    const checkPostHogVariant = () => {
      const posthogVariant = getBuilderVariantFromPostHog(offeringSlug);

      if (posthogVariant) {
        setDeterminedVariant(posthogVariant);

        if (!hasTrackedExperiment.current) {
          trackPostHogBuilderExperiment(offeringSlug, posthogVariant, {
            variant_source: 'posthog_feature_flag',
          });
          hasTrackedExperiment.current = true;
        }

        if (enableRedirect && !hasAttemptedRedirect.current) {
          hasAttemptedRedirect.current = true;
          setShouldRedirect(true);
        }

        if (onVariantDetermined) {
          onVariantDetermined(posthogVariant, 'posthog_feature_flag');
        }
      } else {
        setDeterminedVariant(currentVariant || DEFAULT_VARIANT);

        if (onVariantDetermined) {
          onVariantDetermined(currentVariant || DEFAULT_VARIANT, 'default');
        }
      }

      setIsLoading(false);
    };

    if (typeof window !== 'undefined' && window.posthog?.__loaded) {
      checkPostHogVariant();
    } else {
      let elapsed = 0;
      const POLL_INTERVAL = 50;
      const MAX_WAIT = 1000;
      const pollId = setInterval(() => {
        elapsed += POLL_INTERVAL;
        if (window.posthog?.__loaded || elapsed >= MAX_WAIT) {
          clearInterval(pollId);
          checkPostHogVariant();
        }
      }, POLL_INTERVAL);
      return () => clearInterval(pollId);
    }
  }, [offeringSlug, searchParams, currentVariant, enableRedirect, onVariantDetermined]);

  useEffect(() => {
    if (shouldRedirect && determinedVariant && enableRedirect) {
      const newUrl = buildUrlWithVariant(determinedVariant);
      router.replace(newUrl, { scroll: false });
      setShouldRedirect(false);
    }
  }, [shouldRedirect, determinedVariant, enableRedirect, router, buildUrlWithVariant]);

  const forceVariant = useCallback(
    (variant) => {
      if (!variant) return;

      setDeterminedVariant(variant);
      const newUrl = buildUrlWithVariant(variant);
      router.replace(newUrl, { scroll: false });

      safePostHog.capture('builder_variant_forced', {
        offering_slug: offeringSlug,
        variant_key: variant,
        previous_variant: determinedVariant,
      });
    },
    [offeringSlug, determinedVariant, router, buildUrlWithVariant],
  );

  return {
    isLoading,
    variant: determinedVariant,
    variantSource: searchParams?.get(BUILDER_VARIANT_PARAM) ? 'url_param' : variantSource,
    isPostHogControlled: !!getBuilderVariantFromPostHog(offeringSlug),
    forceVariant,
    buildUrlWithVariant,
  };
};

export default usePostHogBuilderVariant;
